// useStudentHomeData.js
// Data hook backing the student home screen (Option A redesign).
// See docs/superpowers/specs/2026-09-16-home-redesign.md §4.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { FASTAPI_BASE_URL } from "../../../config";
import { HOME } from "../homeTheme";
import { deriveWeek, deriveDueSoon, mapSuggestions } from "./studentHomeDerive";

const INITIAL_LOADING = { week: false, suggestions: false, recent: false };

/**
 * @param {object} params
 * @param {string} params.userEmail
 * @param {string} [params.lang]
 * @param {Array} [params.availableTests]
 * @param {Array} [params.upcomingTests]
 * @param {Array} [params.myTestResults]
 * @param {boolean} [params.hasResume] - true when a Continue card is already
 *   showing, so `resume`-type suggestions should be dropped. Not part of the
 *   spec's headline signature but required by §4's suggestions rule; defaults
 *   to false (keep resume suggestions) when the caller doesn't pass it.
 * @param {(s: string) => string} [params.t]
 */
export default function useStudentHomeData({
  userEmail,
  lang,
  availableTests,
  upcomingTests,
  myTestResults,
  hasResume = false,
  t,
}) {
  const [week, setWeek] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(INITIAL_LOADING);

  // Week: GET .../get-engagement?view_type=daily&limit=30 -> deriveWeek(rows, now)
  useEffect(() => {
    if (!userEmail) return undefined;
    let cancelled = false;
    setLoading((prev) => ({ ...prev, week: true }));

    axios
      .get(`${FASTAPI_BASE_URL}/student/${encodeURIComponent(userEmail)}/get-engagement`, {
        params: { view_type: "daily", limit: 30 },
      })
      .then((res) => {
        if (cancelled) return;
        setWeek(deriveWeek(res.data?.data || [], new Date()));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("useStudentHomeData: failed to fetch engagement", err);
        setWeek(null);
      })
      .finally(() => {
        if (!cancelled) setLoading((prev) => ({ ...prev, week: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [userEmail]);

  // Suggestions: GET .../recommendations?limit=HOME.maxSuggested&lang=...
  useEffect(() => {
    if (!userEmail) return undefined;
    let cancelled = false;
    setLoading((prev) => ({ ...prev, suggestions: true }));

    axios
      .get(`${FASTAPI_BASE_URL}/student/${encodeURIComponent(userEmail)}/recommendations`, {
        params: { limit: HOME.maxSuggested, lang: lang || "en" },
      })
      .then((res) => {
        if (cancelled) return;
        setSuggestions(mapSuggestions(res.data?.recommendations || [], { hasResume }));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("useStudentHomeData: failed to fetch recommendations", err);
        setSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading((prev) => ({ ...prev, suggestions: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [userEmail, lang, hasResume]);

  // Recent: GET .../activity-feed?limit=HOME.maxRecent&lang=... (shape unchanged)
  useEffect(() => {
    if (!userEmail) return undefined;
    let cancelled = false;
    setLoading((prev) => ({ ...prev, recent: true }));

    axios
      .get(`${FASTAPI_BASE_URL}/student/${encodeURIComponent(userEmail)}/activity-feed`, {
        params: { limit: HOME.maxRecent, lang: lang || "en" },
      })
      .then((res) => {
        if (cancelled) return;
        setRecent(res.data?.activities || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("useStudentHomeData: failed to fetch activity feed", err);
        setRecent([]);
      })
      .finally(() => {
        if (!cancelled) setLoading((prev) => ({ ...prev, recent: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [userEmail, lang]);

  // dueSoon is a pure, synchronous derivation of props already fetched
  // elsewhere in AITutorApp - no request of its own.
  const dueSoon = useMemo(
    () =>
      deriveDueSoon({
        availableTests,
        upcomingTests,
        myTestResults,
        now: new Date(),
        t,
        lang,
      }),
    [availableTests, upcomingTests, myTestResults, t, lang]
  );

  return { week, dueSoon, suggestions, recent, loading };
}
